import React, { useEffect, useState } from 'react';
import { Col, Container, Row } from 'react-bootstrap';
import { useParams } from 'react-router-dom';
import Authorize from '../../components/AuthComponent/Authorize';
import SaveButton from '../../components/Buttons/SaveButton';
import ButtonBar from '../../components/Fields/ButtonBar';
import DateControl from '../../components/Fields/DateControl';
import Field from '../../components/Fields/Field';
import FieldLabel from '../../components/Fields/FieldLabel';
import Form from '../../components/Fields/Form';
import SelectListField from '../../components/Fields/SelectListField';
import TextboxField from '../../components/Fields/TextboxField';
import { FormConfig } from '../../components/Fields/Types';
import useSelectListOptions from '../../components/Hooks/SelectListOptionsHook/useSelectListOptions';
import useValidation from '../../components/Hooks/ValidationHook/useValidation';
import { HorizontalTabs } from '../../components/Tabs/HorizontalTabs';
import { ApplicationFunctions, useAuth } from '../../contexts/authContext';
import { useNotifications } from '../../contexts/notificationContext';
import ItemStatusTypeModel from '../../models/ItemStatusTypeModel';
import DataService from '../../services/data.service';
import IdParams from '../../types/IdParams';
import { Result } from '../../types/Result';
import FormatUtils from '../../utils/FormatUtils';
import Unauthorized from '../Authentication/Unauthorized';
import MultiSelectField from './../../components/Fields/MultiSelectField';
import ItemStatusType_tab_ItemStatus from './Tabs/ItemStatusType_tab_ItemStatus';

const ItemStatusTypeView: React.FC = () => {
    const [serverErrors, setServerErrors] = useState<Result>({ isSuccesful: true, messages: [] });
    const { handleResult, handleException } = useNotifications();
    const [isAuthorized, setIsAuthorized] = useState<boolean>(true);

    const [formData, setFormData] = useState<ItemStatusTypeModel>({
        id: 0,
        name: '',
        defaultItemStatusId: 0,
        beginDate: new FormatUtils().formatDateForStorage(new Date()),
        endDate: '',
        itemTypeIds: [],
        concurrencyId: ''
    });

    let idParams: IdParams = useParams();
    let itemStatusTypeId: number | undefined;
    if (idParams.id && !Number.isNaN(parseInt(idParams.id))) {
        itemStatusTypeId = parseInt(idParams.id);
    }

    const itemTypes = useSelectListOptions('api/v1/selectlist/ItemTypes?active=true');
    const itemStatuses = useSelectListOptions('api/v1/selectlist/ItemStatuses?itemStatusTypeId=' + itemStatusTypeId, '-- Select default item-status');

    useEffect(() => {
        if (itemStatusTypeId !== undefined) {
            DataService.getById<ItemStatusTypeModel>('api/v1/ItemStatusTypes/', itemStatusTypeId)
                .then(response => setFormData(response))
                .catch(e => {
                    if (e.response?.data?.status === 401)
                        setIsAuthorized(false);
                    else
                        handleException(e);
                });
        }
    }, [itemStatusTypeId]);

    function submit() {
        if (formData) {
            if (formData.id === 0)
                DataService
                    .create<ItemStatusTypeModel>(`api/v1/ItemStatusTypes/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
            else
                DataService
                    .update<ItemStatusTypeModel>(`api/v1/ItemStatusTypes/`, formData)
                    .then(response => {
                        handleResult(response);
                        setServerErrors(response);
                        if (response.isSuccesful === true && response.data)
                            setFormData(response.data);
                    })
                    .catch(e => handleException(e));
        }
    }

    const formConfig: FormConfig = {
        fields: [
            { name: 'name', label: 'Name', validationRules: [{ type: 'isRequired' }] },
            { name: 'itemTypeIds', label: 'Item types' },
            { name: 'defaultItemStatusId', label: 'Default item-status' },
            { name: 'beginDate', label: 'Active from', validationRules: [{ type: 'isRequired' }, { type: 'isDate' }] },
            { name: 'endDate', label: 'Active Until', labelSize: 'auto', validationRules: [{ type: 'isDate' }] },
        ],
        handleSubmit: submit,
        showErrors: 'always',
    };

    const { getLabelProps, getControlProps, getFieldProps, getSubmitButtonProps } = useValidation(formConfig, formData, serverErrors);

    const { isAuthorizedFor } = useAuth();
    if (itemStatusTypeId === undefined) {
        if (!isAuthorizedFor(ApplicationFunctions.EditItemStatusType)) {
            return (<Unauthorized />);
        }
    }
    else if (!isAuthorized) {
        return (<Unauthorized />);
    }

    return (
        <div className='main-content'>
            <Form title='Itemstatus type' footer={(
                <Row>
                    <Col md='12'>
                        <ButtonBar offset={2}>
                            <Authorize applicationFunction={ApplicationFunctions.EditItemStatusType}>
                                <SaveButton  {...getSubmitButtonProps()} />
                            </Authorize>
                        </ButtonBar>
                    </Col>
                </Row>
            )}>
                <Container fluid>
                    <Row>
                        <Col>
                            <TextboxField {...getFieldProps('name')} />
                            <MultiSelectField {...getFieldProps('itemTypeIds')} options={itemTypes} />
                            <SelectListField {...getFieldProps('defaultItemStatusId')} options={itemStatuses} />
                            <Field {...getLabelProps('beginDate')}>
                                <DateControl {...getControlProps('beginDate')} />
                                <span style={{ padding: '7px 15px' }}><FieldLabel {...getLabelProps('endDate')} /></span>
                                <DateControl {...getControlProps('endDate')} />
                            </Field>
                        </Col>
                    </Row>
                </Container>
            </Form>
            {itemStatusTypeId && (
                <Container fluid>
                    <Row>
                        <Col md='12'>
                            <HorizontalTabs
                                id='vtabs'
                                defaultActiveKey='itemstatuses'
                                tabs={[
                                    {
                                        title: 'Statuses',
                                        eventKey: 'itemstatuses',
                                        component: <ItemStatusType_tab_ItemStatus id={itemStatusTypeId} />,
                                    },
                                ]}
                            />
                        </Col>
                    </Row>
                </Container>
            )}
        </div>
    );
}
export default ItemStatusTypeView;